<template>
	<div ref="selectRoot" class="delivery__group-input-wrapper">
		<div class="input">
			<label :id="labelId" :for="inputId" class="input__label">Номер відділення</label>
			<v-select
				v-model="selectedDepartment"
				:input-id="inputId"
				:options="departmentOptions"
				label="label"
				:filterable="false"
				:clearable="false"
				@search="onSearch"
				@open="syncA11y"
				@close="syncA11y"
				@option:selected="onSelect"
				placeholder="Введи номер відділення (число)"
			>
				<template #no-options="{ searching, search }">
					<span v-if="searching && search">Вибачте, немає відповідних варіантів</span>
					<span v-else>Почніть вводити номер відділення</span>
				</template>
			</v-select>
			<div class="input__error" aria-hidden="true"></div>
			<div :id="statusId" class="visually-hidden" aria-live="polite" aria-atomic="true">
				{{ selectedAnnouncement }}
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { syncVueSelectA11y } from '../utils/vue-select-a11y';

const props = defineProps({
	inputId: {
		type: String,
		required: true
	},
	modelValue: {
		type: Object,
		default: null
	},
	city: {
		type: Object,
		default: null
	},
	invalid: {
		type: Boolean,
		default: false
	},
	errorId: {
		type: String,
		default: ''
	}
});

const emit = defineEmits(['update:modelValue']);

const departmentOptions = ref(Array());
const selectedDepartment = ref(props.modelValue || null);
const selectedAnnouncement = ref('');
const selectRoot = ref(null);

const labelId = computed(() => `${props.inputId}-label`);
const statusId = computed(() => `${props.inputId}-status`);

const syncA11y = () => {
	nextTick(() => {
		syncVueSelectA11y({
			root: selectRoot.value,
			labelId: labelId.value,
			statusId: statusId.value,
			hasSelectedValue: Boolean(selectedDepartment.value?.label),
			invalid: props.invalid,
			errorId: props.errorId,
		});
	});
};

const announceSelected = (label = '') => {
	selectedAnnouncement.value = label ? `Обрано: ${label}` : '';
	syncA11y();
};

const resolveNpConf = () => {
	try {
		if (window['globalConf'] && window['globalNpConf']) {
			return window['globalConf'](window['globalNpConf']);
		}

		if (window['NpConf']) {
			return window['NpConf'];
		}
	} catch (error) {
		console.error('Failed to resolve NP config', error);
	}

	return null;
};

const onSearch = async (query = '') => {
	const term = (query || '').trim();
	if (!term) {
		departmentOptions.value = [];
		return;
	}

	const npConf = resolveNpConf();
	const endpoint = npConf?.url?.getNpDepartment;
	const shopName = npConf?.shop_name;
	const cityId = props.city?.delivery_city_id || sessionStorage.getItem('delivery_city_id');

	if (!endpoint || !shopName || !cityId) {
		departmentOptions.value = [];
		return;
	}

	try {
		const response = await fetch(
			`${endpoint}?query=${encodeURIComponent(term)}&shop_name=${encodeURIComponent(shopName)}&city_id=${encodeURIComponent(cityId)}`
		);
		const data = await response.json();
		departmentOptions.value = Array.isArray(data) ? data : [];
	} catch (error) {
		console.error('Failed to fetch departments', error);
		departmentOptions.value = [];
	}
};

const onSelect = (item = null) => {
	if (!item) {
		return;
	}

	selectedDepartment.value = item;
	emit('update:modelValue', item);
	announceSelected(item.label || '');

	sessionStorage.setItem('department_label', item.label || '');
	sessionStorage.setItem('department_number', item.meta?.number || '');
};

watch(() => props.modelValue, (value) => {
	selectedDepartment.value = value || null;
	announceSelected(selectedDepartment.value?.label || '');
});

watch(() => [props.invalid, props.errorId], syncA11y);

watch(() => props.city?.delivery_city_id || '', (nextCityId, prevCityId) => {
	if (!prevCityId) {
		return;
	}

	if (nextCityId === prevCityId) {
		return;
	}

	departmentOptions.value = [];
	selectedDepartment.value = null;
	emit('update:modelValue', null);
	announceSelected('');

	sessionStorage.setItem('department_label', '');
	sessionStorage.setItem('department_number', '');
});

onMounted(syncA11y);
</script>
